import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Input, Select, Card, Row, Col, Statistic, message, Popconfirm, Tooltip, Modal, Form, Typography } from 'antd';
import { PlusOutlined, EyeOutlined, CheckCircleOutlined, DeleteOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import dayjs from 'dayjs';
import apiClient from '../services/api';
import { AssetTransfer, TransferStats } from '../types/assetTransfer';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;

const AssetTransferPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [transfers, setTransfers] = useState<AssetTransfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<TransferStats | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: '',
    search: '',
  });
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<AssetTransfer | null>(null);
  const [approveForm] = Form.useForm();

  const loadTransfers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
      };
      const response = await apiClient.getAssetTransfers(params) as any;
      setTransfers(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
      }));
    } catch (error: any) {
      // Lấy thông báo lỗi từ response của backend
      let errorMessage = 'Lỗi khi tải danh sách điều chuyển';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Chuyển đổi **text** thành <strong>text</strong> để làm nổi bật
      const formattedMessage = errorMessage.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Sử dụng dangerouslySetInnerHTML để hiển thị HTML
      message.error({
        content: <div dangerouslySetInnerHTML={{ __html: formattedMessage }} />,
        duration: 5
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiClient.getAssetTransferStats() as any;
      setStats(response.data);
    } catch (error: any) {
      message.error('Lỗi khi tải thống kê');
    }
  };

  useEffect(() => {
    loadTransfers();
    loadStats();
  }, [pagination.current, pagination.pageSize, filters]); // loadTransfers and loadStats will be stable after first render

  const handleTableChange = (pagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value: string) => {
    handleFilterChange('search', value);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.deleteAssetTransfer(id);
      message.success('Xóa phiếu điều chuyển thành công');
      loadTransfers();
      loadStats();
    } catch (error: any) {
      // Lấy thông báo lỗi từ response của backend
      let errorMessage = 'Lỗi khi xóa phiếu điều chuyển';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Chuyển đổi **text** thành <strong>text</strong> để làm nổi bật
      const formattedMessage = errorMessage.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Sử dụng dangerouslySetInnerHTML để hiển thị HTML
      message.error({
        content: <div dangerouslySetInnerHTML={{ __html: formattedMessage }} />,
        duration: 5
      });
    }
  };

  const handleApprove = async (values: { approval_document_code: string }) => {
    if (!selectedTransfer) return;

    try {
      await apiClient.approveAssetTransfer(selectedTransfer.id, {
        approval_document_code: values.approval_document_code
      });
      message.success('Duyệt phiếu điều chuyển thành công');
      setApproveModalVisible(false);
      setSelectedTransfer(null);
      approveForm.resetFields();
      loadTransfers();
      loadStats();
    } catch (error: any) {
      // Lấy thông báo lỗi từ response của backend
      let errorMessage = 'Lỗi khi duyệt phiếu điều chuyển';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Chuyển đổi **text** thành <strong>text</strong> để làm nổi bật
      const formattedMessage = errorMessage.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Sử dụng dangerouslySetInnerHTML để hiển thị HTML
      message.error({
        content: <div dangerouslySetInnerHTML={{ __html: formattedMessage }} />,
        duration: 5
      });
    }
  };

  const showApproveModal = (transfer: AssetTransfer) => {
    setSelectedTransfer(transfer);
    setApproveModalVisible(true);
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'approved': return 'green';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Chờ duyệt';
      case 'approved': return 'Đã duyệt (Hoàn thành)';
      case 'cancelled': return 'Đã hủy';
      default: return status;
    }
  };

  const columns = [
    {
      title: 'Mã phiếu',
      dataIndex: 'code',
      key: 'code',
      ellipsis: true,
      render: (code: string) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{code}</span>
      ),
    },
    {
      title: 'Người bàn giao',
      dataIndex: 'from_employee_name',
      key: 'from_employee_name',
      width: 150,
    },
    {
      title: 'Người nhận',
      dataIndex: 'to_employee_name',
      key: 'to_employee_name',
      width: 150,
    },
    {
      title: 'Số lượng tài sản',
      dataIndex: 'asset_count',
      key: 'asset_count',
      ellipsis: true,
      align: 'center' as const,
      render: (count: number) => <Tag color="blue">{count || 0}</Tag>,
    },
    {
      title: 'Lý do',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      ellipsis: true,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      ellipsis: true,
      fixed: 'right' as const,
      render: (text: string, record: AssetTransfer) => (
        <Space size="small">
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/asset-transfers/${record.id}`)}
            />
          </Tooltip>

          {record.status === 'pending' && (
            <>
              <Tooltip title="Duyệt">
                <Button
                  type="text"
                  icon={<CheckCircleOutlined />}
                  style={{ color: '#52c41a' }}
                  onClick={() => showApproveModal(record)}
                />
              </Tooltip>
              <Tooltip title="Xóa">
                <Popconfirm
                  title="Bạn có chắc muốn xóa phiếu điều chuyển này?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Có"
                  cancelText="Không"
                >
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    style={{ color: '#ff4d4f' }}
                  />
                </Popconfirm>
              </Tooltip>
            </>
          )}


        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, display: 'inline-block' }}>
          Điều chuyển tài sản
        </Title>
        {user?.role !== 'employee_user' && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/asset-transfers/create')}
            style={{ float: 'right' }}
            size="large"
          >
            Tạo phiếu điều chuyển
          </Button>
        )}
      </div>

      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card>
              <Statistic title="Tổng số phiếu" value={stats.total} valueStyle={{ color: '#1890ff' }} />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic title="Chờ duyệt" value={stats.pending} valueStyle={{ color: '#faad14' }} />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic title="Đã duyệt" value={stats.approved} valueStyle={{ color: '#52c41a' }} />
            </Card>
          </Col>
        </Row>
      )}

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Search
              placeholder="Tìm kiếm theo mã phiếu, lý do..."
              onSearch={handleSearch}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Trạng thái"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="pending">Chờ duyệt</Option>
              <Option value="approved">Đã duyệt (Hoàn thành)</Option>
              <Option value="cancelled">Đã hủy</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Button icon={<FileExcelOutlined />} onClick={() => message.info('Tính năng xuất Excel đang được phát triển')}>
              Xuất Excel
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={transfers}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} phiếu điều chuyển`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Modal Duyệt Phiếu Điều Chuyển */}
      <Modal
        title="Duyệt Phiếu Điều Chuyển"
        open={approveModalVisible}
        onCancel={() => {
          setApproveModalVisible(false);
          setSelectedTransfer(null);
          approveForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={approveForm}
          layout="vertical"
          onFinish={handleApprove}
        >
          <Form.Item
            name="approval_document_code"
            label="Hồ sơ bàn sao từ văn phòng điện tử"
            rules={[{ required: true, message: 'Vui lòng nhập hồ sơ bàn sao' }]}
          >
            <Input placeholder="Nhập mã hồ sơ bàn sao..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setApproveModalVisible(false);
                setSelectedTransfer(null);
                approveForm.resetFields();
              }}>
                Hủy
              </Button>
              <Button type="primary" htmlType="submit">
                Duyệt
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>


    </div>
  );
};

export default AssetTransferPage; 